import React, { useState, useEffect, useRef, useCallback } from 'react'
import { logReadingEvent } from '../../../helpers/readingEvents'
import AudioPlayer from '../RecordingTeacher/AudioPlayer'
import fox from '../../../Assets/Images/Trialpage/fox.jpg'
import Contact from '../../../Assets/Images/Trialpage/contact.jpg'

const AudioSection = ({ selectedStudent, students, expertAudioUrl, socketres, setAudioCurrentTime, unifiedSession, storyId }) => {
  // Find the selected student's data
  const student = students?.find(s => s.id === selectedStudent)

  // Phase 2C: model-audio play/stop events. Callbacks are stable (refs) so
  // AudioPlayer's play/pause effect doesn't re-run on every render, and the
  // pause callback only logs after a real play (AudioPlayer also calls it on mount).
  const modelPlayingRef = useRef(false)
  const eventCtxRef = useRef({})
  eventCtxRef.current = {
    sessionId: unifiedSession?.id,
    studentId: selectedStudent,
    contentId: storyId,
  }
  const handleModelPlay = useCallback(() => {
    if (modelPlayingRef.current) return
    modelPlayingRef.current = true
    const c = eventCtxRef.current
    logReadingEvent(c.sessionId, 'model_audio_played', {
      student_id: c.studentId,
      reading_content_id: c.contentId,
    })
  }, [])
  const handleModelPause = useCallback(() => {
    if (!modelPlayingRef.current) return
    modelPlayingRef.current = false
    const c = eventCtxRef.current
    logReadingEvent(c.sessionId, 'model_audio_stopped', {
      student_id: c.studentId,
      reading_content_id: c.contentId,
    })
  }, [])
  
  // Updated to handle different possible locations of the recording URL
  const recordedAudio = React.useMemo(() => {
    console.log("Looking for recordedAudio:");
    console.log("- studentId:", selectedStudent);
    console.log("- student data:", student);
    
    // First check student_audio directly from the student object
    if (student?.student_audio) {
      console.log("Found in student.student_audio:", student.student_audio);
      return student.student_audio;
    }
    
    // Check if socketres is a direct recording response
    if (socketres && socketres.recording) {
      console.log("Found direct recording in socketres:", socketres.recording);
      return socketres.recording;
    }
    
    // Check if socketres has the submitted_assignment structure
    if (socketres && 
        socketres.submitted_assignment && 
        socketres.submitted_assignment.student === selectedStudent && 
        socketres.submitted_assignment.recording) {
      console.log("Found in socketres.submitted_assignment.recording:", socketres.submitted_assignment.recording);
      return socketres.submitted_assignment.recording;
    }
    
    // Check if socketres itself is the submitted_assignment
    if (socketres && socketres.student === selectedStudent && socketres.recording) {
      console.log("Found in socketres as submitted_assignment:", socketres.recording);
      return socketres.recording;
    }
    
    // Check student data
    if (student?.submitted_assignment?.recording) {
      console.log("Found in student.submitted_assignment.recording:", student.submitted_assignment.recording);
      return student.submitted_assignment.recording;
    }
    
    if (student?.recording) {
      console.log("Found in student.recording:", student.recording);
      return student.recording;
    }
    
    if (student?.recorded_audio) {
      console.log("Found in student.recorded_audio:", student.recorded_audio);
      return student.recorded_audio;
    }
    
    // If student has recordings array (some APIs return this format)
    if (student?.recordings && student.recordings.length > 0) {
      console.log("Found in student.recordings[0]:", student.recordings[0].url || student.recordings[0]);
      return student.recordings[0].url || student.recordings[0];
    }
    
    // If there's a reading_content field with recording
    if (student?.reading_content?.recording) {
      console.log("Found in student.reading_content.recording:", student.reading_content.recording);
      return student.reading_content.recording;
    }
    
    console.log("No recording found for student");
    return null;
  }, [student, socketres, selectedStudent]);
  
  const [isExpertAudioLoaded, setIsExpertAudioLoaded] = useState(false)
  const [isRecordedAudioLoaded, setIsRecordedAudioLoaded] = useState(false)
  const [expertAudioError, setExpertAudioError] = useState(null)
  const [recordedAudioError, setRecordedAudioError] = useState(null)

  // Use the student's expert_audio URL if available, otherwise fall back to expertAudioUrl prop
  const fullExpertAudioUrl = React.useMemo(() => {
    // Prioritize student's expert_audio if available
    if (student?.expert_audio) {
      return student.expert_audio.trim();
    }
    
    // For textbook content, use the expertAudioUrl from the current reading content
    if (expertAudioUrl) {
      return expertAudioUrl.trim();
    }
    
    return null;
  }, [expertAudioUrl, student]);

  // Preload expert audio
  useEffect(() => {
    if (fullExpertAudioUrl) {
      const audio = new Audio();
      
      const handleCanPlayThrough = () => {
        console.log('Expert audio loaded successfully');
        setIsExpertAudioLoaded(true);
        setExpertAudioError(null);
      };

      const handleError = (error) => {
        console.error('Error loading expert audio:', error);
        setIsExpertAudioLoaded(false);
        setExpertAudioError('Failed to load expert audio');
      };

      const handleLoadStart = () => {
        console.log('Expert audio loading started');
        setIsExpertAudioLoaded(false);
        setExpertAudioError(null);
      };

      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('error', handleError);
      
      // Set the source and start loading
      audio.src = fullExpertAudioUrl;
      audio.load();

      return () => {
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('error', handleError);
        audio.src = ''; // Clear the source
      };
    }
  }, [fullExpertAudioUrl]);

  // Preload recorded audio
  useEffect(() => {
    if (recordedAudio) {
      const audio = new Audio();
      
      const handleCanPlayThrough = () => {
        console.log('Recorded audio loaded successfully');
        setIsRecordedAudioLoaded(true);
        setRecordedAudioError(null);
      };

      const handleError = (error) => {
        console.error('Error loading recorded audio:', error);
        setIsRecordedAudioLoaded(false);
        setRecordedAudioError('Failed to load recorded audio');
      };

      const handleLoadStart = () => {
        console.log('Recorded audio loading started');
        setIsRecordedAudioLoaded(false);
        setRecordedAudioError(null);
      };

      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('error', handleError);
      
      // Set the source and start loading
      audio.src = recordedAudio;
      audio.load();

      return () => {
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('error', handleError);
        audio.src = ''; // Clear the source
      };
    }
  }, [recordedAudio]);

  // Handle WebSocket messages for recordings
  useEffect(() => {
    if (socketres) {
      // Check if this is a recording completion message
      if (socketres.detail === "Recording Submitted Successfully." && 
          socketres.submitted_assignment?.recording) {
        console.log("New recording received via WebSocket", socketres.submitted_assignment.recording);
      }
      
      // Log errors from WebSocket
      if (socketres.error) {
        console.error("WebSocket error:", socketres.error);
      }
    }
  }, [socketres]);

  // Debug the URLs
  React.useEffect(() => {
    console.log('Debug AudioSection:');
    console.log('- Selected Student ID:', selectedStudent);
    console.log('- Student Data:', student);
    console.log('- Student Audio:', student?.student_audio);
    console.log('- Has Submitted:', student?.has_submitted);
    console.log('- Recorded Audio:', recordedAudio);
    console.log('- Expert Audio URL:', fullExpertAudioUrl);
    console.log('- WebSocket Response:', socketres);
  }, [student, selectedStudent, recordedAudio, fullExpertAudioUrl, socketres]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
      <div className="flex items-center text-gray-900 justify-start w-full">
        <div className="w-full">
          {fullExpertAudioUrl && (
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-1 border border-slate-100/50">
              <AudioPlayer
                onTimeUpdate={setAudioCurrentTime}
                title="Model Audio"
                audioUrl={fullExpertAudioUrl}
                imageUrl={fox}
                className="origin-left"
                isExpertAudio={true}
                onPlay={handleModelPlay}
                onPause={handleModelPause}
                onError={(error) => {
                  console.error('Audio Player Error:', error);
                  console.error('Failed URL:', fullExpertAudioUrl);
                  setExpertAudioError('Failed to load model audio');
                }}
              />
              {expertAudioError && (
                <div className="text-red-500 text-xs px-4 pb-2">{expertAudioError}</div>
              )}
            </div>
          )}
        </div>
      </div>
  
      <div className="flex items-center justify-start w-full">
        <div className="w-full">
          {recordedAudio ? (
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-1 border border-slate-100/50 transition-all duration-500 animate-in fade-in slide-in-from-bottom-2">
              <AudioPlayer
                title="My Audio" 
                audioUrl={recordedAudio}
                imageUrl={Contact}
                className="origin-left"
                onError={(error) => {
                  console.error('Recorded Audio Player Error:', error);
                  setRecordedAudioError('Failed to load your recording');
                }}
              />
              {recordedAudioError && (
                <div className="text-red-500 text-xs px-4 pb-2">{recordedAudioError}</div>
              )}
            </div>
          ) : (
            <div className="w-full h-full min-h-[80px] rounded-2xl border-2 border-dashed border-slate-100 flex items-center justify-center bg-slate-50/30">
              <span className="text-slate-400 text-xs font-medium uppercase tracking-widest">Awaiting Recording</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioSection